import { Controller } from "@hotwired/stimulus"
import Cropper from "cropperjs"
import { DirectUpload } from "@rails/activestorage"

// Choose a photo, upload it, frame it.
//
// The order matters and it is not the obvious one. Cropping in the browser
// FIRST would be cheaper — a smaller upload, one step — but it only works for
// formats a browser can decode, and an iPhone shoots HEIC, which none of them
// can. So the file goes up first, the server hands back a JPEG preview, and
// the crop happens against that.
//
// What that buys, beyond HEIC: the crop is COORDINATES rather than a new
// file. The photo is stored once at full quality and every variant crops on
// the way out, so re-framing later costs nothing and nothing is thrown away.
//
// The upload is direct — browser to storage — so uploading before framing
// holds no Rails worker open, and Active Storage purges anything abandoned.
//
// The plain file field stays in the DOM and keeps working with no JS: the
// server accepts either a file or a signed id.
export default class extends Controller {
  static targets = [ "input", "field", "image", "dialog", "progress", "bar",
                     "preview", "drop", "cropX", "cropY", "cropSize" ]
  static values = { url: String, previewUrl: String }

  disconnect() {
    this.teardown()
  }

  // ---- Choosing -----------------------------------------------------------

  chosen(event) {
    const file = event.target.files?.[0]
    if (file) this.accept(file)
  }

  // Dropping a file on the photo box does the same as choosing one.
  dropped(event) {
    event.preventDefault()
    this.highlight(false)

    const file = event.dataTransfer?.files?.[0]
    if (!file) return

    // A drop bypasses the file field's accept attribute, so this is the only
    // check on what was dropped.
    if (!file.type.startsWith("image/")) {
      return this.fail("That doesn't look like an image.")
    }

    this.accept(file)
  }

  // preventDefault on dragover is what makes an element a drop target at all
  // — without it the browser navigates to the file instead.
  over(event) {
    event.preventDefault()
    this.highlight(true)
  }

  leave(event) {
    // Moving over a CHILD fires dragleave on the parent, so the highlight
    // flickers unless the pointer has genuinely left the box.
    if (this.hasDropTarget && this.dropTarget.contains(event.relatedTarget)) return

    this.highlight(false)
  }

  highlight(on) {
    if (this.hasDropTarget) this.dropTarget.classList.toggle("is-dropping", on)
  }

  // ---- Uploading, then framing --------------------------------------------

  async accept(file) {
    this.teardown()
    this.clearError()
    this.busy(true)

    try {
      const signedId = await this.upload(file)
      const preview = await this.preview(signedId)

      this.busy(false)
      this.openCropper(preview)
    } catch (error) {
      this.busy(false)
      this.fail(error.message || "That photo could not be uploaded.")
      console.error("Photo upload failed", error)
    }
  }

  // Straight to storage. The form will post the signed id rather than a file.
  upload(file) {
    return new Promise((resolve, reject) => {
      new DirectUpload(file, this.urlValue, this).create((error, attributes) => {
        if (error) return reject(error)

        this.fieldTarget.value = attributes.signed_id
        // Both fields are called recipe[photo] and exactly one may post. The
        // hidden one is disabled until it holds a signed id; the file field
        // is cleared so the original is not sent alongside it.
        this.fieldTarget.disabled = false
        this.inputTarget.value = ""
        resolve(attributes.signed_id)
      })
    })
  }

  // The server's JPEG rendering of what was just uploaded — converted from
  // HEIC where it has to be, and limited to something sane to draw a box on.
  async preview(signedId) {
    const response = await fetch(
      `${this.previewUrlValue}?signed_id=${encodeURIComponent(signedId)}`,
      { headers: { Accept: "application/json" } }
    )
    const body = await response.json()

    if (!response.ok) throw new Error(body.error || "That photo could not be read.")

    return body.url
  }

  openCropper(url) {
    this.imageTarget.src = url
    this.dialogTarget.hidden = false

    // Constructed after the image has a source: Cropper measures it on
    // creation, and a src set afterwards leaves the selection sized against
    // nothing.
    this.imageTarget.onload = () => {
      this.cropper = new Cropper(this.imageTarget, { template: this.template() })
    }
  }

  // Cropper v2 builds its own markup from a template, so the shape of the
  // control lives here rather than in the ERB.
  //
  // aspect-ratio and initial-aspect-ratio both 1: the first locks resizing to
  // a square, the second makes the box start as one. Every variant is square,
  // so there is nothing to choose between.
  template() {
    return `
      <cropper-canvas background style="height: 22rem">
        <cropper-image rotatable scalable translatable></cropper-image>
        <cropper-shade hidden></cropper-shade>
        <cropper-handle action="select" plain></cropper-handle>
        <cropper-selection initial-coverage="0.9" aspect-ratio="1"
                           initial-aspect-ratio="1" movable resizable>
          <cropper-grid role="grid" bordered covered></cropper-grid>
          <cropper-crosshair centered></cropper-crosshair>
          <cropper-handle action="move" theme-color="rgba(255,255,255,0.35)"></cropper-handle>
          <cropper-handle action="n-resize"></cropper-handle>
          <cropper-handle action="e-resize"></cropper-handle>
          <cropper-handle action="s-resize"></cropper-handle>
          <cropper-handle action="w-resize"></cropper-handle>
          <cropper-handle action="ne-resize"></cropper-handle>
          <cropper-handle action="nw-resize"></cropper-handle>
          <cropper-handle action="se-resize"></cropper-handle>
          <cropper-handle action="sw-resize"></cropper-handle>
        </cropper-selection>
      </cropper-canvas>
    `
  }

  // ---- Confirming ---------------------------------------------------------

  // Save the rectangle, not a file.
  //
  // As FRACTIONS, and of two different things: size is a fraction of the
  // preview's short edge, while x and y are fractions of the room the
  // selection has left to move in. That second part is what makes every
  // value valid by construction — measured against the full width instead, a
  // selection at x = 0.5 starts past the middle and runs off the edge, and
  // libvips refuses the crop outright. See Recipe#photo_crop_box.
  async confirm() {
    const selection = this.cropper?.getCropperSelection()
    const image = this.cropper?.getCropperImage()
    if (!selection || !image) return

    const { width, height } = image.getBoundingClientRect()
    const short = Math.min(width, height)
    if (!short) return

    const size = selection.width / short
    const roomX = width - selection.width
    const roomY = height - selection.height

    this.cropXTarget.value = roomX > 0 ? clamp(selection.x / roomX) : 0
    this.cropYTarget.value = roomY > 0 ? clamp(selection.y / roomY) : 0
    this.cropSizeTarget.value = clamp(size)

    // Show the framing straight away rather than after a save.
    if (this.hasPreviewTarget) {
      const canvas = await selection.$toCanvas({ width: 160, height: 160 })
      this.previewTarget.src = canvas.toDataURL("image/jpeg", 0.7)
      this.previewTarget.classList.remove("hidden")
    }

    this.dialogTarget.hidden = true
    this.teardown()
  }

  // Backing out leaves the recipe as it was: no signed id, no crop, and the
  // hidden field disabled again so nothing stale can post.
  cancel() {
    this.dialogTarget.hidden = true
    this.inputTarget.value = ""
    this.fieldTarget.value = ""
    this.fieldTarget.disabled = true
    this.cropXTarget.value = this.cropYTarget.value = this.cropSizeTarget.value = ""
    this.teardown()
  }

  // ---- Plumbing -----------------------------------------------------------

  // DirectUpload calls this so a delegate can watch the request. The upload
  // is the slow part on a phone, so it is the part with a progress bar.
  directUploadWillStoreFileWithXHR(request) {
    request.upload.addEventListener("progress", (event) => {
      if (!event.lengthComputable) return
      this.barTarget.style.width = `${(event.loaded / event.total) * 100}%`
    })
  }

  busy(on) {
    this.progressTarget.hidden = !on
    if (!on) this.barTarget.style.width = "0%"
  }

  fail(message) {
    this.clearError()
    this.error = document.createElement("p")
    this.error.className = "mt-2 text-sm text-protein"
    this.error.textContent = message
    this.progressTarget.after(this.error)
  }

  clearError() {
    this.error?.remove()
    this.error = null
  }

  teardown() {
    this.cropper?.destroy()
    this.cropper = null
  }
}

// Fractions only. The model validates this too — the values arrive from a
// form field, which is to say from anywhere — but there is no reason to send
// something known to be out of range.
function clamp(value) {
  return Math.min(1, Math.max(0, value)).toFixed(5)
}
